import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  InvestmentPropertyDepositsModel,
  InvestmentPropertyModel,
} from './investment-property.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const INVESTMENT_ENDPOINT = '/investmentproperty';
const V2_ENDPOINT = '/api/v2/real-state-investments';
const DEPOSIT_ENDPOINT = '/investmentpropertydeposit';

@Injectable({
  providedIn: 'root',
})
export class InvestmentPropertyService {
  private readonly http = inject(HttpClient);

  public async downloadFileImage(uid: string): Promise<Blob> {
    const params = new HttpParams().set('investmentpropertyId', uid);

    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentproperty/file/downloadimage', {
        responseType: 'blob',
        params,
      })
    );

    return new Blob([response], { type: 'image/png' });
  }

  public async downloadFilePDF(uid: string): Promise<Blob> {
    const params = new HttpParams().set('investmentpropertyId', uid);

    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + '/investmentproperty/file/downloadpdf', {
        responseType: 'blob',
        params,
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  public get(uid: string): Promise<InvestmentPropertyModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<InvestmentPropertyModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se pudo cargar la inversión inmobiliaria'
    );
  }

  public async list(
    offset?: number,
    limit?: number
  ): Promise<ResultModel<InvestmentPropertyModel>> {
    let params = new HttpParams();

    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultModel<InvestmentPropertyModel>>>(
        environment.apiUrl + INVESTMENT_ENDPOINT + '/list',
        { params }
      )
    );

    return response.data;
  }

  public async add(investmentproperty: InvestmentPropertyModel): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<boolean>>(
        environment.apiUrl + INVESTMENT_ENDPOINT,
        investmentproperty
      )
    );

    return response.data;
  }

  public async edit(investmentproperty: InvestmentPropertyModel): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<boolean>>(
        environment.apiUrl + INVESTMENT_ENDPOINT,
        investmentproperty
      )
    );

    return response.data;
  }

  public async delete(uid: string): Promise<boolean> {
    const params = new HttpParams().set('investmentpropertyId', uid);

    const response = await firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + INVESTMENT_ENDPOINT, {
        params,
      })
    );

    return response.data;
  }

  public depositList(
    investmentpropertyUid: string
  ): Promise<InvestmentPropertyDepositsModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<InvestmentPropertyDepositsModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(investmentpropertyUid) + '/deposits'
        )
      ),
      'No se pudieron cargar los pagos de la inversión'
    );
  }

  public async depositCreate(
    deposit: InvestmentPropertyDepositsModel
  ): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    );

    return response.data;
  }

  public async depositUpdate(
    deposit: InvestmentPropertyDepositsModel
  ): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, deposit)
    );

    return response.data;
  }

  public async depositDelete(uid: string): Promise<boolean> {
    const params = new HttpParams().set('investmentpropertydepositId', uid);

    const response = await firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEPOSIT_ENDPOINT, {
        params,
      })
    );

    return response.data;
  }
}